import { formatMoney } from '../lib/format.js';
import { useApp } from '../store/app.js';

type Size = 'inherit' | 'small' | 'medium' | 'large';
type Tone = 'auto' | 'inherit' | 'neutral' | 'muted' | 'positive' | 'negative';

interface MoneyProps {
  value: number;
  currency?: string;
  size?: Size;
  tone?: Tone;
  signDisplay?: 'auto' | 'never' | 'always';
  decimals?: boolean;
}

const SIZES: Record<Size, string | undefined> = {
  inherit: undefined,
  small: '13px',
  medium: '15px',
  large: '20px',
};

/**
 * Every monetary value goes through here: tabular figures so the width never
 * jumps, one place that honours "hide amounts", and one place that decides
 * when a number is allowed to be coloured. §5
 */
export function Money({ value, currency, size = 'inherit', tone = 'inherit', signDisplay, decimals }: MoneyProps) {
  const hidden = useApp((s) => s.preferences?.hideAmounts ?? false);
  const userCurrency = useApp((s) => s.user?.currency ?? 'ILS');

  const resolved = tone === 'auto' ? (value > 0 ? 'positive' : 'neutral') : tone;
  const classes = [
    'money',
    resolved === 'positive' ? 'money--positive' : '',
    resolved === 'negative' ? 'money--negative' : '',
    resolved === 'muted' ? 'money--muted' : '',
    hidden ? 'money--hidden' : '',
  ].filter(Boolean).join(' ');

  const text = formatMoney(value, {
    currency: currency ?? userCurrency,
    signDisplay,
    compactDecimals: decimals === undefined ? undefined : !decimals,
  });

  return (
    <span className={classes} style={{ fontSize: SIZES[size] }} aria-label={hidden ? 'סכום מוסתר' : text}>
      {text}
    </span>
  );
}
